import { useImage } from "react-image";

import { CircularProgress } from "@mui/material";

import { ErrorBoundary } from "@sentry/react";

import { FaIcon } from "@features/ui";
import { Image } from "@models/Image";
import { cn } from "@utils/tailwind";

import { cloudinary } from "./cloudinary";
import imageSizeOptions, { ImageSize } from "./imageSizeOptions";

/** Inline placeholder — avoids network + keeps src always defined for react-image. */
const SRC_NO_IMAGE =
  "data:image/png;base64,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";

type CldImageProps = Omit<React.HTMLProps<HTMLImageElement>, "size"> & {
  src?: string;
  publicId?: string;
  image?: Image | null;
  size?: ImageSize;
  page?: number;
  resourceType?: "image" | "video";
};

const ImageLoadingFallback = ({ className }: { className?: string }) => (
  <div className={cn("flex items-center justify-center", className)}>
    <CircularProgress size={16} className="text-neutral-300" />
  </div>
);

const LazyImage = ({ src, ...props }: CldImageProps & { src: string }) => {
  const {
    src: renderSrc,
    isLoading,
    error,
  } = useImage({
    srcList: [src, SRC_NO_IMAGE],
    // Suspense + undefined/failing src thrash under React 19 (brandhub-ui pattern).
    useSuspense: false,
  });

  if (isLoading) {
    return <ImageLoadingFallback className={props.className} />;
  }

  return <img src={error ? SRC_NO_IMAGE : renderSrc} alt="" {...props} />;
};

const CldImage = ({
  src: providedSrc,
  image,
  publicId,
  size,
  page,
  resourceType,
  ...props
}: CldImageProps) => {
  let src = providedSrc;
  if (!src) {
    const id = publicId ?? image?.cloudinaryId;
    if (id) {
      const sizeOptions = size ? imageSizeOptions[size] : undefined;
      src = cloudinary.url(id + ".jpg", {
        ...sizeOptions,
        secure: true,
        page: page && page > 1 ? page : undefined,
        resource_type:
          resourceType ?? (image?.type === "video" ? "video" : undefined),
      });
    }
  }

  // Always pass a concrete URL into react-image (never undefined).
  const resolvedSrc = src ?? SRC_NO_IMAGE;

  return (
    <ErrorBoundary
      fallback={() => (
        <div
          className={cn(
            "flex flex-col gap-1 items-center justify-center text-neutral-400",
            props.className
          )}
        >
          <FaIcon icon="image" className="text-xl" />
          <p className="text-xs font-medium">ERROR</p>
        </div>
      )}
    >
      <LazyImage src={resolvedSrc} {...props} />
    </ErrorBoundary>
  );
};

export default CldImage;
